import {ColorPickerValueType} from "primereact/colorpicker";
import React, {useEffect, useState} from 'react';
import {checkIfArraysLengthIsSame} from "utils/utils";

import {AggregationType, ChartData, ChartType, Dataset} from "components/dashboard/dataset-window/models/types";
import {createIStatsRows, createPieData} from "components/dashboard/dataset-window/utils/utils";
import {IDataSet} from "components/dashboard/dataset-window/models/interfaces";

import BasicChart from "components/dashboard/charts/BasicChart";
import {configLineChartData} from "components/dashboard/charts/ChartConfig/configLineChartData";
import {convertDataToChartsData, getAllDatasetsTimestamps} from "components/dashboard/charts/utils";
import {configBoxPlotData} from "components/dashboard/charts/ChartConfig/configBoxPlotData";
import {configHistogramData} from "components/dashboard/charts/ChartConfig/configHistogramData";
import {configScatterChartData} from "components/dashboard/charts/ChartConfig/configScatterChart";
import {configPieChartData} from "components/dashboard/charts/ChartConfig/configPieChartData";
import {configMultiScatterChartData} from "components/dashboard/charts/ChartConfig/configMultiScatterChart";
import Stats from "components/dashboard/charts/Stats";
import RawDatasetsTable from "components/dashboard/charts/RawDatasetsTable";
import {DatasetDataInfoResponseArrayToPlotDataExArray} from "components/dashboard/charts/bar-chart/DatasetBarChartDataConverter";
import DatasetBarChart from "components/dashboard/charts/bar-chart/DatasetBarChart";
import {mapArraysByTimestampToSameLength} from "../charts/multi-scatter-chart/MultiScatterConfig";
import {DatasetDataInfoResponse} from "api/model/dataset/DatasetDataInfoResponse";


type ChartRendererProps = {
    selectedChartType: ChartType,

    isLoading: boolean,
    data: IDataSet[],
    isError: boolean

    dataPointCount: number,

    colorPallet: { key: string; value: Array<string> },

    singleAxis: { x: string, y: string[] }
    multiAxis: { x: string, y: string, color: string, size: string }
    colorScale: boolean,

    selectedDatasetsID: Array<Dataset>
    aggregationType: AggregationType
    datesRange: Date[]
    selectedColor: ColorPickerValueType

    windowWidth: number,
    windowHeight: number
}

const ChartRenderer = React.memo(({
                                      selectedChartType,
                                      isLoading,
                                      data,
                                      colorPallet,
                                      dataPointCount,
                                      singleAxis,
                                      multiAxis,
                                      isError,
                                      selectedDatasetsID,
                                      aggregationType,
                                      windowHeight,
                                      windowWidth,
                                      colorScale,
                                      datesRange,
                                      selectedColor,
                                  }: ChartRendererProps) => {
    const [printAllowed, setPrintAllowed] = useState(false)
    const [convertedCharData, setConvertedCharData] = useState<ChartData[]>([])

    useEffect(() => {
        if (data && data.length > 0) {
            setConvertedCharData(convertDataToChartsData(data))
        } else {
            setConvertedCharData([]);
        }
    }, [data])

    useEffect(() => {
        const moreThanZeroTraces = convertedCharData.length > 0;
        setPrintAllowed(false)

        if ((selectedChartType === "LINE" ||
            selectedChartType === "BAR" ||
            selectedChartType === "BOX" ||
            selectedChartType === "HISTOGRAM" ||
            selectedChartType === "RAW") && moreThanZeroTraces) {
            setPrintAllowed(true)
        } else if ((selectedChartType === "PIE" || selectedChartType === "STATS") && data) {
            setPrintAllowed(true)
        } else if (selectedChartType === "SCATTER" && moreThanZeroTraces &&
            singleAxis.x !== "" && singleAxis.y.length > 0 && getScatterConfig(convertedCharData)) {
            setPrintAllowed(true)
        } else if (selectedChartType === "MULTISCATTER" && moreThanZeroTraces &&
            multiAxis.x !== "" && multiAxis.y !== "" && getMultiScatterConfig(convertedCharData)) {
            setPrintAllowed(true)
        }
    }, [
        convertedCharData, selectedChartType, dataPointCount,
        singleAxis.x, singleAxis.y,
        multiAxis.x, multiAxis.y, multiAxis.color, multiAxis.size
    ])

    const getScatterConfig = (chartData: ChartData[]): { X: ChartData; Y: Array<ChartData> } | undefined => {
        const datasetXAxis: ChartData | undefined = chartData.find((s: any) => s.id === singleAxis.x);
        const datasetsYAxis: Array<ChartData | undefined> = singleAxis.y.map((yAxisID) => {
            return chartData.find((s: any) => {
                return s.id === yAxisID;
            });
        });


        if (datasetXAxis != null && datasetsYAxis != null) {
            return {
                X: datasetXAxis,
                Y: datasetsYAxis as Array<ChartData>
            }
        }

        return undefined
    }

    const getMultiScatterConfig = (chartData: ChartData[]): { X: ChartData, Y: ChartData, color?: ChartData, size?: ChartData } | undefined => {
        const datasetXAxis: ChartData | undefined = chartData.find((s: any) => s.id === multiAxis.x);
        const datasetYAxis: ChartData | undefined = chartData.find((s: any) => s.id === multiAxis.y);
        const datasetColorAxis: ChartData | undefined = chartData.find((s: any) => s.id === multiAxis.color);
        const datasetSizeAxis: ChartData | undefined = chartData.find((s: any) => s.id === multiAxis.size);

        const scatterData: { X?: ChartData; Y?: ChartData; color?: ChartData; size?: ChartData } = {
            X: datasetXAxis,
            Y: datasetYAxis,
            color: datasetColorAxis,
            size: datasetSizeAxis,
        };
        if (scatterData.X !== undefined && scatterData.Y !== undefined) {
            return scatterData as { X: ChartData, Y: ChartData, color: ChartData, size: ChartData };
        }
        return undefined
    }

    if (isError) {
        return <div>Error while fetching data...</div>
    }

    if (!printAllowed || selectedDatasetsID.length === 0) {
        return <></>
    }

    if (selectedChartType === "LINE") {
        return <BasicChart
            plotData={configLineChartData(convertedCharData, colorPallet.value)}
            props={{loading: isLoading, identifiers: [], hideRefresh: true, height: windowHeight}}
        />
    }

    if (selectedChartType === "BAR") {
        return <DatasetBarChart data={DatasetDataInfoResponseArrayToPlotDataExArray(data as DatasetDataInfoResponse[])}
                                width={windowWidth} height={windowHeight}
        />
    }

    if (selectedChartType === "BOX") {
        return <BasicChart
            plotData={configBoxPlotData(convertedCharData, colorPallet.value)}
            props={{loading: isLoading, identifiers: [], hideRefresh: true, height: windowHeight}}
        />
    }

    if (selectedChartType === "PIE") {
        return <BasicChart
            plotData={configPieChartData(createPieData(data || [], aggregationType), colorPallet.value)}
            props={{loading: isLoading, identifiers: [], hideRefresh: true, height: windowHeight}}
        />
    }

    if (selectedChartType === "SCATTER") {
        const scatterData = getScatterConfig(convertedCharData);
        if (!scatterData) {
            return <></>
        }
        return <BasicChart
            plotData={
                configScatterChartData(
                    scatterData.X,
                    scatterData.Y,
                    colorPallet.value,
                    datesRange,
                    selectedColor)
            }
            props={{loading: isLoading, identifiers: [], hideRefresh: true, height: windowHeight}}
        />
    }
    if (selectedChartType === "MULTISCATTER") {
        let multiScatterData = getMultiScatterConfig(convertedCharData);

        if (!multiScatterData) {
            return <></>
        }

        if (!checkIfArraysLengthIsSame(multiScatterData.X?.valueArr,
            multiScatterData.Y?.valueArr,
            multiScatterData.color?.valueArr,
            multiScatterData.size?.valueArr)) {
            const tempData = mapArraysByTimestampToSameLength(data as DatasetDataInfoResponse[], multiAxis) as IDataSet[];

            if(tempData) {
                const tempConvertedData = convertDataToChartsData(tempData);
                multiScatterData = getMultiScatterConfig(tempConvertedData)
            }

        }

        if(multiScatterData?.X === undefined || multiScatterData?.Y === undefined){
            return <></>
        }

        return <BasicChart
            plotData={configMultiScatterChartData(
                {
                    xData: multiScatterData.X,
                    yData: multiScatterData.Y,
                    colorData: multiScatterData.color,
                    sizeData: multiScatterData.size,
                    selectedColorPalette: colorPallet.value,
                    colorscale: colorScale
                }
            )}
            props={{
                loading: isLoading,
                identifiers: [],
                hideRefresh: true,
                height: windowHeight,
                legend: {orientation: "v"},
                xaxisName: multiScatterData.X.unit||""
            }}
        />
    }

    if (selectedChartType === "HISTOGRAM") {
        return <BasicChart plotData={configHistogramData(convertedCharData, colorPallet.value)}
                           props={{loading: isLoading, identifiers: [], hideRefresh: true, height: windowHeight}}
        />
    }

    if (selectedChartType === "STATS") {
        if (!data) {
            return <></>
        }
        return <Stats statsArr={createIStatsRows(data)}/>
    }

    if (selectedChartType === "RAW") {
        if (!data) {
            return <></>
        }
        return <RawDatasetsTable data={getAllDatasetsTimestamps(data)}/>
    }

    return <></>
});

export default ChartRenderer;